import React from "react";
import { Paper, Tabs, Tab } from "@material-ui/core";
import { css, cx } from "@emotion/css";
import { Switch, Route, useHistory, useParams, useRouteMatch, Redirect } from "react-router-dom";
import Sidebar, { EntityType } from "./components/Sidebar";
import Editor from "./components/Editor";
import Preview from "./components/Preview";
import { useQuestions, useBooks, useQuestionGroups } from "./queries";
import { Area } from "../../types";
import { Question } from "./types/common";
import { Book, QuestionGroup } from "./types/persisted";

type SelectionType = "question" | "book" | "questionGroup";

type Selection =
  | { type: SelectionType; id: number }
  | null;

type ExpandedPane = "editor" | "preview" | null;

type EditedEntity = Question | Book | QuestionGroup | null;

const tabToPath: Record<EntityType, string> = {
  books: "/health-forms/books",
  questionGroups: "/health-forms/question-groups",
  questions: "/health-forms/questions",
};

const pathToTab: Record<string, EntityType> = {
  books: "books",
  "question-groups": "questionGroups",
  questions: "questions",
};

const tabToSelectionType: Record<EntityType, SelectionType> = {
  books: "book",
  questionGroups: "questionGroup",
  questions: "question",
};

const HealthFormsContent: React.VFC = () => {
  const history = useHistory();
  const params = useParams<{ tab?: string; id?: string }>();
  const [expandedPane, setExpandedPane] = React.useState<ExpandedPane>(null);
  const [selectionByTab, setSelectionByTab] = React.useState<Record<EntityType, number | null>>({
    books: null,
    questionGroups: null,
    questions: null,
  });
  const [editedEntity, setEditedEntity] = React.useState<EditedEntity>(null);

  const questionsQuery = useQuestions();
  const booksQuery = useBooks();
  const questionGroupsQuery = useQuestionGroups();

  const tab: EntityType = params.tab ? (pathToTab[params.tab] ?? "books") : "books";

  const firstIdByTab: Record<EntityType, number | undefined> = {
    books: booksQuery.data?.[0]?.id,
    questionGroups: questionGroupsQuery.data?.[0]?.id,
    questions: questionsQuery.data?.[0]?.id,
  };

  const currentId = params.id ? parseInt(params.id, 10) : selectionByTab[tab] ?? firstIdByTab[tab];
  const selection: Selection = currentId
    ? { type: tabToSelectionType[tab], id: currentId }
    : null;

  React.useEffect(() => {
    const paramId = params.id ? parseInt(params.id, 10) : null;
    if (paramId && paramId !== selectionByTab[tab]) {
      setSelectionByTab((prev) => ({ ...prev, [tab]: paramId }));
    }
  }, [params.id, tab, selectionByTab]);

  React.useEffect(() => {
    setEditedEntity(null);
  }, [selection?.id]);

  React.useEffect(() => {
    if (!params.id && currentId) {
      history.replace(`${tabToPath[tab]}/${currentId}`);
    }
  }, [params.id, currentId, tab, history]);

  const handleTabChange = (_: React.ChangeEvent<unknown>, newValue: EntityType) => {
    const savedId = selectionByTab[newValue];
    if (savedId) {
      history.push(`${tabToPath[newValue]}/${savedId}`);
    } else {
      history.push(tabToPath[newValue]);
    }
  };

  const handleSelect = (sel: Selection) => {
    if (sel) {
      history.push(`${tabToPath[tab]}/${sel.id}`);
    } else {
      history.push(tabToPath[tab]);
    }
  };

  const handleCreated = (sel: Selection) => {
    if (sel) {
      const selTab = sel.type === "question" ? "questions" : sel.type === "book" ? "books" : "questionGroups";
      history.push(`${tabToPath[selTab]}/${sel.id}`);
    }
  };

  const toggleExpand = (pane: "editor" | "preview") => {
    setExpandedPane(expandedPane === pane ? null : pane);
  };

  return (
    <div className={styles.container}>
      <Tabs value={tab} onChange={handleTabChange} indicatorColor="primary" className={styles.tabs}>
        <Tab label={`Books (${booksQuery.data?.length ?? "..."})`} value="books" />
        <Tab label={`Question Groups (${questionGroupsQuery.data?.length ?? "..."})`} value="questionGroups" />
        <Tab label={`Questions (${questionsQuery.data?.length ?? "..."})`} value="questions" />
      </Tabs>

      <div className={styles.panels}>
        <Paper className={cx(styles.sidebar, expandedPane && styles.hidden)}>
          <Sidebar selection={selection} onSelect={handleSelect} onCreated={handleCreated} tab={tab} />
        </Paper>

        <Paper className={cx(styles.editor, expandedPane === "editor" && styles.expanded, expandedPane === "preview" && styles.hidden)}>
          <Editor
            selection={selection}
            isExpanded={expandedPane === "editor"}
            onToggleExpand={() => toggleExpand("editor")}
            onEdit={setEditedEntity}
          />
        </Paper>

        <Paper className={cx(styles.preview, expandedPane === "preview" && styles.expanded, expandedPane === "editor" && styles.hidden)}>
          <Preview
            selection={selection}
            isExpanded={expandedPane === "preview"}
            onToggleExpand={() => toggleExpand("preview")}
            editedEntity={editedEntity}
            showTestToggle={tab === "books"}
          />
        </Paper>
      </div>
    </div>
  );
};

const HealthForms: React.VFC = () => {
  const { path } = useRouteMatch();

  return (
    <Switch>
      <Route exact path={path}>
        <Redirect to={Area.HealthForms_Books} />
      </Route>
      <Route exact path={`${path}/:tab`}>
        <HealthFormsContent />
      </Route>
      <Route path={`${path}/:tab/:id`}>
        <HealthFormsContent />
      </Route>
    </Switch>
  );
};

export default HealthForms;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 64px);
    padding: 16px;
    box-sizing: border-box;
  `,
  tabs: css`
    margin-bottom: 16px;
    border-bottom: 1px solid #e0e0e0;
  `,
  panels: css`
    display: flex;
    flex: 1;
    gap: 16px;
    min-height: 0;
  `,
  sidebar: css`
    width: 250px;
    flex-shrink: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `,
  editor: css`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    position: relative;
  `,
  preview: css`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    position: relative;
  `,
  expanded: css`
    flex: 1;
    width: auto;
  `,
  hidden: css`
    display: none;
  `,
};
